import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoClose, IoSearchOutline, IoTimeOutline } from "react-icons/io5";
import "./LiveClassSession.css";

const API_BASE_URL = process.env.REACT_APP_API_BASEURL;

const MEETING_PLATFORMS = ["Zoom", "Google Meet", "Microsoft Teams", "Other"];
const SESSION_STATUSES = ["upcoming", "live", "completed", "cancelled"];

const EMPTY_FORM = {
  liveClassId: "",
  teacherName: "",
  sessionTitle: "",
  sessionDescription: "",
  startDateTime: "",
  endDateTime: "",
  meetingPlatform: "Zoom",
  meetingLink: "",
  meetingId: "",
  meetingPasscode: "",
  maxParticipants: 100,
  notes: "",
};

// Converts an ISO datetime string to the value <input type="datetime-local"> expects.
const toLocalInputValue = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

const formatReadableDateTime = (isoString) => {
  if (!isoString) return "--";
  return new Date(isoString).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const LiveClassSession = () => {
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [liveClassFilter, setLiveClassFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const authHeaders = {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  };

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/admin/live-classes-session`,authHeaders);
      setSessions(response.data?.data || []);
      setPageError("");
    } catch (error) {
      console.log(error);
      setPageError("Couldn't load sessions. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveClasses = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/live-classes`);
      setLiveClasses(response.data?.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchSessions();
    fetchLiveClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const liveClassTitleById = useMemo(() => {
    const map = {};
    liveClasses.forEach((lc) => {
      map[lc._id] = lc.title;
    });
    return map;
  }, [liveClasses]);

  const filteredSessions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return sessions.filter((session) => {
      const matchesTerm =
        !term ||
        session.sessionTitle?.toLowerCase().includes(term) ||
        session.teacherName?.toLowerCase().includes(term);

      const sessionLiveClassId =
        session.liveClassId?._id || session.liveClassId;

      const matchesLiveClass =
        liveClassFilter === "all" || sessionLiveClassId === liveClassFilter;

      const matchesStatus =
        statusFilter === "all" || session.status === statusFilter;

      return matchesTerm && matchesLiveClass && matchesStatus;
    });
  }, [sessions, searchTerm, liveClassFilter, statusFilter]);

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (session) => {
    setEditingId(session._id);
    setFormData({
      liveClassId: session.liveClassId?._id || session.liveClassId || "",
      teacherName: session.teacherName || "",
      sessionTitle: session.sessionTitle || "",
      sessionDescription: session.sessionDescription || "",
      startDateTime: toLocalInputValue(session.startDateTime),
      endDateTime: toLocalInputValue(session.endDateTime),
      meetingPlatform: session.meetingPlatform || "Zoom",
      meetingLink: session.meetingLink || "",
      meetingId: session.meetingId || "",
      meetingPasscode: session.meetingPasscode || "",
      maxParticipants: session.maxParticipants ?? 100,
      notes: session.notes || "",
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setIsModalOpen(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.liveClassId) errors.liveClassId = "Choose a live class.";
    if (!formData.teacherName.trim()) errors.teacherName = "Teacher name is required.";
    if (!formData.sessionTitle.trim()) errors.sessionTitle = "Session title is required.";
    if (!formData.startDateTime) errors.startDateTime = "Start date & time is required.";
    if (!formData.endDateTime) errors.endDateTime = "End date & time is required.";
    if (
      formData.startDateTime &&
      formData.endDateTime &&
      new Date(formData.endDateTime) <= new Date(formData.startDateTime)
    ) {
      errors.endDateTime = "End time must be after the start time.";
    }
    if (!formData.meetingLink.trim()) errors.meetingLink = "Meeting link is required.";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      ...formData,
      maxParticipants: Number(formData.maxParticipants) || 100,
    };

    try {
      setIsSaving(true);
      if (editingId) {
        await axios.put(
          `${API_BASE_URL}/admin/live-classes-session/${editingId}`,
          payload,
          authHeaders
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/admin/live-classes-session`,
          payload,
          authHeaders
        );
      }
      setIsModalOpen(false);
      fetchSessions();
    } catch (error) {
      console.log(error);
      setFormErrors({
        submit:
          error?.response?.data?.msg || "Something went wrong. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Do you want to delete this session? This cannot be undone."
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE_URL}/admin/live-classes-session/${id}`, authHeaders);
      setSessions((prev) => prev.filter((session) => session._id !== id));
    } catch (error) {
      console.log(error);
      alert("Failed to delete session.");
    }
  };

  const handleStatusChange = async (id, status) => {
    // Optimistic update so the dropdown feels instant.
    const previous = sessions;
    setSessions((prev) =>
      prev.map((session) => (session._id === id ? { ...session, status } : session))
    );
    try {
      await axios.put(
        `${API_BASE_URL}/admin/live-classes-session-status/${id}`,
        { status },
        authHeaders
      );
    } catch (error) {
      console.log(error);
      setSessions(previous);
      alert("Couldn't update the status. Please try again.");
    }
  };

  return (
    <div id="add-subject-inner-wrapper">
      <div className="superheader">
        <div className="subjectHeaderdiv">
          <h5>Live Class Sessions</h5>
          <div className="subjectdiv2">
            <button className="handleNewSubject" onClick={openCreateModal}>
              + Add New Session
            </button>
            <button
              className="allSubjects"
              onClick={() => navigate("/live-class-list")}
            >
              All Live Classes
            </button>
          </div>
        </div>

        <div className="form-sub-container mt-4" id="form">
          <div className="lcs-toolbar">
            <div className="lcs-search">
              <IoSearchOutline className="lcs-search-icon" />
              <input
                type="text"
                placeholder="Search by session title or teacher"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <select
              className="lcs-filter-select"
              value={liveClassFilter}
              onChange={(e) => setLiveClassFilter(e.target.value)}
            >
              <option value="all">All live classes</option>
              {liveClasses.map((lc) => (
                <option key={lc._id} value={lc._id}>
                  {lc.title}
                </option>
              ))}
            </select>

            <select
              className="lcs-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              {SESSION_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <h4 className="subject-detail">
            Sessions{" "}
            <span className="lcs-count">({filteredSessions.length})</span>
          </h4>

          {pageError && <div className="lcs-alert">{pageError}</div>}

          <div className="lcs-table-wrap">
            <table className="classes-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Live Class</th>
                  <th>Session</th>
                  <th>Teacher</th>
                  <th>Schedule</th>
                  <th>Platform</th>
                  <th>Participants</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="lcs-empty-row">
                      Loading sessions...
                    </td>
                  </tr>
                ) : filteredSessions.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="lcs-empty-row">
                      No sessions match your search. Try adjusting the filters
                      or add a new session.
                    </td>
                  </tr>
                ) : (
                  filteredSessions.map((session, idx) => (
                    <tr key={session._id}>
                      <td>{idx + 1}</td>
                      <td>
                        {session.liveClassId?.title ||
                          liveClassTitleById[session.liveClassId] ||
                          "--"}
                      </td>
                      <td>
                        <div className="lcs-session-title">
                          {session.sessionTitle}
                        </div>
                        {session.sessionDescription && (
                          <div className="lcs-session-desc">
                            {session.sessionDescription.slice(0, 40)}
                            {session.sessionDescription.length > 40 ? "..." : ""}
                          </div>
                        )}
                      </td>
                      <td>{session.teacherName}</td>
                      <td>
                        <div className="lcs-schedule">
                          <IoTimeOutline />
                          <div>
                            <div>{formatReadableDateTime(session.startDateTime)}</div>
                            <div className="lcs-schedule-end">
                              to {formatReadableDateTime(session.endDateTime)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{session.meetingPlatform}</td>
                      <td>
                        {session.currentParticipants ?? 0}/
                        {session.maxParticipants ?? "--"}
                      </td>
                      <td>
                        <select
                          className={`lcs-status-select lcs-status-${session.status}`}
                          value={session.status}
                          onChange={(e) =>
                            handleStatusChange(session._id, e.target.value)
                          }
                        >
                          {SESSION_STATUSES.map((status) => (
                            <option key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="table-action-icons">
                        <i
                          className="fa-solid fa-file-pen"
                          title="Edit session"
                          onClick={() => openEditModal(session)}
                        ></i>
                        <i
                          className="fa-solid fa-trash-can"
                          title="Delete session"
                          onClick={() => handleDelete(session._id)}
                        ></i>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="lcs-modal-overlay" onClick={closeModal}>
          <div className="lcs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lcs-modal-header">
              <h4>{editingId ? "Edit Session" : "Add New Session"}</h4>
              <button
                className="lcs-modal-close"
                onClick={closeModal}
                aria-label="Close"
              >
                <IoClose />
              </button>
            </div>

            <form className="lcs-form" onSubmit={handleSubmit}>
              {formErrors.submit && (
                <div className="lcs-alert">{formErrors.submit}</div>
              )}

              <div className="lcs-form-grid">
                <div className="lcs-field lcs-field-full">
                  <label>Live Class *</label>
                  <select
                    name="liveClassId"
                    value={formData.liveClassId}
                    onChange={handleFormChange}
                  >
                    <option value="">Select a live class</option>
                    {liveClasses.map((lc) => (
                      <option key={lc._id} value={lc._id}>
                        {lc.title}
                      </option>
                    ))}
                  </select>
                  {formErrors.liveClassId && (
                    <span className="lcs-field-error">{formErrors.liveClassId}</span>
                  )}
                </div>

                <div className="lcs-field">
                  <label>Teacher Name *</label>
                  <input
                    type="text"
                    name="teacherName"
                    value={formData.teacherName}
                    onChange={handleFormChange}
                    placeholder="e.g. Priya Sharma"
                  />
                  {formErrors.teacherName && (
                    <span className="lcs-field-error">{formErrors.teacherName}</span>
                  )}
                </div>

                <div className="lcs-field">
                  <label>Session Title *</label>
                  <input
                    type="text"
                    name="sessionTitle"
                    value={formData.sessionTitle}
                    onChange={handleFormChange}
                    placeholder="e.g. Algebra: Quadratic Equations"
                  />
                  {formErrors.sessionTitle && (
                    <span className="lcs-field-error">{formErrors.sessionTitle}</span>
                  )}
                </div>

                <div className="lcs-field lcs-field-full">
                  <label>Session Description</label>
                  <textarea
                    name="sessionDescription"
                    value={formData.sessionDescription}
                    onChange={handleFormChange}
                    rows={2}
                    placeholder="What will this session cover?"
                  />
                </div>

                <div className="lcs-field">
                  <label>Start Date & Time *</label>
                  <input
                    type="datetime-local"
                    name="startDateTime"
                    value={formData.startDateTime}
                    onChange={handleFormChange}
                  />
                  {formErrors.startDateTime && (
                    <span className="lcs-field-error">{formErrors.startDateTime}</span>
                  )}
                </div>

                <div className="lcs-field">
                  <label>End Date & Time *</label>
                  <input
                    type="datetime-local"
                    name="endDateTime"
                    value={formData.endDateTime}
                    onChange={handleFormChange}
                  />
                  {formErrors.endDateTime && (
                    <span className="lcs-field-error">{formErrors.endDateTime}</span>
                  )}
                </div>

                <div className="lcs-field">
                  <label>Meeting Platform</label>
                  <select
                    name="meetingPlatform"
                    value={formData.meetingPlatform}
                    onChange={handleFormChange}
                  >
                    {MEETING_PLATFORMS.map((platform) => (
                      <option key={platform} value={platform}>
                        {platform}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="lcs-field">
                  <label>Max Participants</label>
                  <input
                    type="number"
                    min="1"
                    name="maxParticipants"
                    value={formData.maxParticipants}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="lcs-field lcs-field-full">
                  <label>Meeting Link *</label>
                  <input
                    type="text"
                    name="meetingLink"
                    value={formData.meetingLink}
                    onChange={handleFormChange}
                    placeholder="https://zoom.us/j/..."
                  />
                  {formErrors.meetingLink && (
                    <span className="lcs-field-error">{formErrors.meetingLink}</span>
                  )}
                </div>

                <div className="lcs-field">
                  <label>Meeting ID</label>
                  <input
                    type="text"
                    name="meetingId"
                    value={formData.meetingId}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="lcs-field">
                  <label>Meeting Passcode</label>
                  <input
                    type="text"
                    name="meetingPasscode"
                    value={formData.meetingPasscode}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="lcs-field lcs-field-full">
                  <label>Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows={2}
                    placeholder="Internal notes for this session"
                  />
                </div>
              </div>

              <div className="lcs-modal-actions">
                <button
                  type="button"
                  className="lcs-btn-secondary"
                  onClick={closeModal}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="lcs-btn-primary" disabled={isSaving}>
                  {isSaving
                    ? "Saving..."
                    : editingId
                    ? "Save Changes"
                    : "Create Session"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveClassSession;